import type { ThemeConfig } from 'antd';
import { theme as antdTheme, ConfigProvider } from 'antd';
import React, { createContext, useEffect, useMemo, useState } from 'react';
import worktoolTheme, { darkModeTokens } from '../../config/theme';
import { useAppTheme } from './index';

export type ThemeMode = 'light' | 'dark';

type ThemeContextValue = ReturnType<typeof useAppTheme> & {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
  isDark: boolean;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useThemeContext(): ThemeContextValue {
  const ctx = React.useContext(ThemeContext);

  // Fallback: provide theme even without Provider
  const fallbackTheme = useAppTheme();
  return (
    ctx ?? {
      ...fallbackTheme,
      mode: 'light' as ThemeMode,
      setMode: () => {},
      toggleMode: () => {},
      isDark: false,
    }
  );
}

/**
 * Enhanced ThemeProvider with FastGPT-inspired design system
 * Features:
 * - Dark mode support with proper token management
 * - Persistent theme preference
 * - Enhanced theme utilities and layout presets
 * - Smooth theme transitions
 */
export const ThemeProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    // Initialize from localStorage or system preference
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('theme-mode');
      if (stored === 'light' || stored === 'dark') return stored;

      // Check system preference
      if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
    }
    return 'light';
  });

  // Persist theme preference
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('theme-mode', mode);

      // Update document class for CSS-based theming
      document.documentElement.classList.toggle('dark', mode === 'dark');
      document.documentElement.setAttribute('data-theme', mode);
    }
  }, [mode]);

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e: MediaQueryListEvent) => {
        // Only auto-switch if no manual preference is stored
        const stored = localStorage.getItem('theme-mode');
        if (!stored) {
          setModeState(e.matches ? 'dark' : 'light');
        }
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    // Return undefined explicitly for the case where window is not available
    return undefined;
  }, []);

  const setMode = (newMode: ThemeMode) => {
    setModeState(newMode);
  };

  const toggleMode = () => {
    setModeState((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  // Create enhanced theme configuration with dark mode support
  const themeConfig: ThemeConfig = useMemo(() => {
    const baseTheme = { ...worktoolTheme };

    if (mode === 'dark') {
      // Apply dark mode algorithm and custom tokens
      baseTheme.algorithm = [antdTheme.darkAlgorithm];
      baseTheme.token = {
        ...baseTheme.token,
        ...darkModeTokens,
      };
    } else {
      baseTheme.algorithm = [antdTheme.defaultAlgorithm];
    }

    return baseTheme;
  }, [mode]);

  const value = useAppTheme();
  const isDark = mode === 'dark';

  const ctxValue = useMemo(
    () => ({
      ...value,
      mode,
      setMode,
      toggleMode,
      isDark,
    }),
    [value, mode, isDark],
  );

  return (
    <ConfigProvider theme={themeConfig}>
      <ThemeContext.Provider value={ctxValue}>
        <div
          className={`theme-provider ${mode}`}
          style={{
            minHeight: '100vh',
            backgroundColor: value.token.colorBgLayout,
            color: value.token.colorText,
            transition: 'background-color 0.3s ease, color 0.3s ease',
          }}
        >
          <style>
            {`
            /* Responsive helpers derived from theme tokens */
            ${value.media.down('md')} {
              .ant-table {
                width: 100%;
                overflow-x: auto;
              }
              .ant-table-thead > tr > th,
              .ant-table-tbody > tr > td {
                white-space: pre;
              }
              .ant-table-thead > tr > th > span,
              .ant-table-tbody > tr > td > span {
                display: block;
              }
            }
            `}
          </style>
          {children}
        </div>
      </ThemeContext.Provider>
    </ConfigProvider>
  );
};

export default ThemeProvider;
